import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import residentialImage from "../../images/residential_img.jfif";
import plottedImage from "../../images/plotted_img.jpg";
import commercialImage from "../../images/commercial_img.jpg";
import { searchVentures } from "../services/ventureApi";
import { hasActiveSession } from "../services/authApi";
import LoginModal from "./LoginModal";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
  faHeart,
  faLocationDot,
  faStar,
  faCircleCheck,
  faRulerCombined,
  faCalendarDays,
  faBuilding,
  faShieldHalved,
  faTableCells,
  faList,
  faChevronDown,
  faCopy,
  faArrowRight,
  faMap
} from "@fortawesome/free-solid-svg-icons";

const FALLBACK_IMAGE = "https://images.unsplash.com/photo-1632400990400-416d5460f337?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=600";

const getCategoryImage = (project, projectType) => {
  const category = [
    findDatabaseValue(project, ["Category", "ProjectCategory"]),
    projectType
  ].map((value) => formatDatabaseValue(value).toLowerCase()).join(" ");

  if (category.includes("plotted")) {
    return plottedImage;
  }
  if (category.includes("commercial")) {
    return commercialImage;
  }
  if (category.includes("residential")) {
    return residentialImage;
  }

  return project?.ImageUrl || project?.ThumbnailUrl || FALLBACK_IMAGE;
};

const STATUS_CLASS_MAP = {
  "new launch": "new-launch",
  "ready to move": "ready",
  "under construction": "construction"
};

const PAGE_SIZE = 12;
const MAP_PAGE_SIZE = 500;
const INDIA_MAP_BOUNDS = {
  minLatitude: 6.5,
  maxLatitude: 37.5,
  minLongitude: 68,
  maxLongitude: 97.5
};

function DetailSection({ icon, title, subtitle, children }) {
  return <section style={{ padding: 18, border: "1px solid #e1e8f0", borderRadius: 15, background: "#fff", boxShadow: "0 4px 16px rgba(20,44,78,.05)" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
        <span style={{ width: 38, height: 38, display: "grid", placeItems: "center", flexShrink: 0, borderRadius: 10, background: "#edf3fb", color: "#123b70" }}>
          <FontAwesomeIcon icon={icon} />
        </span>
        <div>
          <h3 style={{ margin: 0, color: "#17243a", fontSize: 17, fontWeight: 750 }}>{title}</h3>
          <p style={{ margin: "3px 0 0", color: "#66758b", fontSize: 12.5 }}>{subtitle}</p>
        </div>
      </div>
      {children}
    </section>;
}

function DetailFields({ fields, columns = 3 }) {
  return <div className="vd-project-detail-fields" style={{ "--detail-columns": columns }}>
      {fields.map(({ label, value }) => <div key={label} style={{ minWidth: 0, padding: 12, border: "1px solid #eaf0f6", borderRadius: 10, background: "#f8fafd" }}>
          <div style={{ marginBottom: 5, color: "#66758b", fontSize: 11.5 }}>{label}</div>
          <div style={{ color: value === "-" || value === "Not supplied by API" ? "#8995a5" : "#17243a", fontSize: 13.5, fontWeight: 700, overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}>{value || "-"}</div>
        </div>)}
    </div>;
}

function DetailStatusList({ fields }) {
  return <div className="vd-project-status-list">
      {fields.map(({ label, value }) => {
        const displayValue = value === "No" ? "-" : value;
        return <div key={label} style={{ padding: 13, border: "1px solid #e1e8f0", borderRadius: 10 }}>
          <div style={{ marginBottom: 7, color: "#66758b", fontSize: 11.5 }}>{label}</div>
          <div style={{ color: displayValue === "Yes" ? "#16845b" : "#59677b", fontSize: 13, fontWeight: 750 }}>{displayValue || "-"}</div>
        </div>;
      })}
    </div>;
}

const formatProjectDateTime = (value) => {
  if (!value) {
    return "-";
  }

  const raw = String(value).trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
  if (!match) {
    return formatProjectDate(value);
  }

  const [, year, month, day, hours, minutes] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  const formattedDate = date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  return hours ? `${formattedDate} · ${hours}:${minutes}` : formattedDate;
};

const formatNumberValue = (value, maximumFractionDigits = 2) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? new Intl.NumberFormat("en-IN", { maximumFractionDigits }).format(numericValue)
    : formatDatabaseValue(value);
};

function PropertyCard({ p, faved, onFav, onDetails }) {
  return <div className="vd-card">
      <div className="vd-card-thumb">
        <img src={p.img} alt={p.name} />
        <span className={`vd-status-badge ${p.statusClass}`}>{p.status}</span>
        <button className={`vd-fav-btn ${faved ? "active" : ""}`} onClick={onFav} aria-label="Favourite">
          <FontAwesomeIcon icon={faHeart} />
        </button>
        <span className="vd-card-type">
          {p.projectType}
        </span>
      </div>
      <div className="vd-card-body">
        <div className="vd-card-meta">
          <div className="vd-card-tags">
            <div className="vd-tags">
              {p.tags.map((t) => <span key={t.key} className={`vd-tag ${t.key}`} title={t.tooltip}>{t.label}</span>)}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", marginTop: 5, justifyContent: "space-between", gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div className="vd-card-name" style={{ marginBottom: 2 }}>{p.name}</div>
            <div className="vd-card-builder" style={{ fontSize: 12.5, color: "#667085" }}>{p.builder}</div>
          </div>        
        </div>

        <div className="vd-card-loc" style={{ marginTop: 2, display: "flex", alignItems: "flex-start", gap: 6, fontSize: 12.5, color: "#475467", lineHeight: 1.4 }}>
          <FontAwesomeIcon icon={faLocationDot} style={{ fontSize: 10, color: "#98a2b3", marginTop: 3, flexShrink: 0 }} />
          <span style={{ wordBreak: "break-word" }}>{p.location}</span>
        </div>



        <div className="pjt_details" style={{ marginTop: 5, display: "grid", gap: 8, fontSize: 12.5, color: "#344054", background: "#f8fafc", border: "1px solid #e4e7ec", borderRadius: 12, padding: "10px 12px" }}>
          <div className="brb" style={{ display: "grid", gridTemplateColumns: "1fr", rowGap: 3 }}>
            <strong style={{ color: "#475467", fontWeight: 600 }}>{p.registrationDisplay.label}</strong>
            <span style={{ fontWeight: 700, color: "#0f172a", overflowWrap: "anywhere" }}>{p.registrationDisplay.value}</span>
          </div>
          <div className="brb" style={{ display: "grid", gridTemplateColumns: "1fr", rowGap: 3 }}>
            <strong style={{ color: "#475467", fontWeight: 600 }}>Proposed Completion</strong>
            <span style={{ fontWeight: 700, color: "#0f172a" }}>{p.proposedCompletionDate}</span>
          </div>
          <div className="brb" style={{ display: "grid", gridTemplateColumns: "1fr", rowGap: 3 }}>
            <strong style={{ color: "#475467", fontWeight: 600 }}>LP / Plan Approval No.</strong>
            <span style={{ fontWeight: 700, color: "#0f172a", overflowWrap: "anywhere" }}>{p.planApprovalNumber}</span>
          </div>
        </div>

        

        <div className="vd-ai-chip" style={{ display: "none" }}>
          <FontAwesomeIcon icon={faStar} style={{ fontSize: 10, flexShrink: 0 }} />
          <span>
            <strong>AI Insight:</strong> {p.aiLabel}
            {p.aiDetail ? <>
                {" | "}
                <span style={{ fontWeight: 400 }}>{p.aiDetail}</span>
              </> : null}
          </span>
        </div>
        <div className="vd-card-actions vd-card-actions-detail" style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
          <button className="vd-act-btn detail" type="button" onClick={onDetails} style={{ borderRadius: 10, fontWeight: 700 }}>
            View Details
            <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>;
}
function MapPane({ projects, error }) {
  const mapContainerRef = useRef(null);
  const mappedProjects = useMemo(() => projects.map((project) => ({
    project,
    coordinates: getProjectCoordinates(project.projectData)
  })).filter(({ coordinates }) => coordinates), [projects]);

  useEffect(() => {
    if (error || !mapContainerRef.current || mappedProjects.length === 0) {
      return undefined;
    }

    const map = L.map(mapContainerRef.current, {
      scrollWheelZoom: false
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(map);

    const markerIcon = L.divIcon({
      className: "vd-leaflet-marker-icon",
      html: '<span class="vd-leaflet-pin"></span>',
      iconSize: [24, 32],
      iconAnchor: [12, 30]
    });
    const bounds = L.latLngBounds([]);

    mappedProjects.forEach(({ project, coordinates }) => {
      const marker = L.marker([coordinates.latitude, coordinates.longitude], { icon: markerIcon }).addTo(map);
      const popup = document.createElement("div");
      const projectName = document.createElement("strong");
      projectName.textContent = project.name;
      const directionsLink = document.createElement("a");
      directionsLink.href = `https://www.google.com/maps?q=${coordinates.latitude},${coordinates.longitude}`;
      directionsLink.target = "_blank";
      directionsLink.rel = "noopener noreferrer";
      directionsLink.textContent = "Open in Google Maps";
      popup.append(projectName, document.createElement("br"), directionsLink);
      marker.bindPopup(popup);
      bounds.extend([coordinates.latitude, coordinates.longitude]);
    });

    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [24, 24], maxZoom: 14 });
    }

    const resizeObserver = new ResizeObserver(() => map.invalidateSize());
    resizeObserver.observe(mapContainerRef.current);

    return () => {
      resizeObserver.disconnect();
      map.remove();
    };
  }, [error, mappedProjects]);

  if (error) {
    return <div className="vd-map-wrap vd-map-empty" role="alert">{error}</div>;
  }

  if (!mappedProjects.length) {
    return <div className="vd-map-wrap vd-map-empty" role="status">
        No projects with valid map coordinates are available for this search.
      </div>;
  }

  return <div className="vd-map-wrap">
      <div ref={mapContainerRef} className="vd-leaflet-map" role="application" aria-label={`Map showing ${mappedProjects.length} project locations`} />
      <div className="vd-map-status" role="status">Showing {mappedProjects.length} project locations</div>
    </div>;
}
const formatPriceValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return `\u20B9 ${new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 0
    }).format(value)}`;
  }

  const valueText = String(value).trim();
  return valueText.startsWith("\u20B9") ? valueText : `\u20B9 ${valueText}`;
};

const formatProjectDate = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const raw = String(value).trim();

  if (!raw || /^(n\/a|null|undefined)$/i.test(raw)) {
    return "-";
  }

  const numericValue = Number(raw);
  const parsed = Number.isFinite(numericValue) && numericValue >= 20000 && numericValue <= 100000
    ? new Date(Date.UTC(1899, 11, 30) + Math.floor(numericValue) * 86400000)
    : new Date(raw);

  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

const normalizeApprovals = (approvals) => {
  if (Array.isArray(approvals)) {
    return Array.from(new Set(approvals.flatMap((approval) => normalizeApprovals(approval))));
  }

  if (typeof approvals === "string") {
    const normalized = approvals.replace(/\bRERAHMDA\b/gi, "RERA, HMDA");
    return Array.from(new Set(normalized.split(/[,|]/).map((entry) => entry.trim()).filter(Boolean)));
  }

  return [];
};

const isEnabled = (value) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  return typeof value === "string" && /^(true|1|yes|y)$/i.test(value.trim());
};

const deriveStatus = (project) => {
  const formattedStatus = formatDatabaseValue(project?.ProjectStatus);
  const dbStatus = formattedStatus === "-" ? "" : formattedStatus;

  if (isEnabled(project?.IsNewLaunch)) {
    return { status: dbStatus || "New Launch", statusClass: "new-launch" };
  }

  if (isEnabled(project?.IsReadyToMove)) {
    return { status: dbStatus || "Ready to Move", statusClass: "ready" };
  }

  if (isEnabled(project?.IsUnderConstruction)) {
    return { status: dbStatus || "Under Construction", statusClass: "construction" };
  }

  const status = dbStatus || "Project";
  const statusClass = STATUS_CLASS_MAP[String(status).toLowerCase()] || "construction";
  return { status, statusClass };
};

const getPlanApprovalNumber = (project) => {
  const rawValue = findDatabaseField(project, ["PlanApprovalNumber", "PlanApprovalNo", "PlanNo", "ApprovalNumber", "LpApprovalNumber", "LpNumber", "LPNumber"]);
  return String(rawValue).trim();
};

const getProjectSearchQuery = (builderName, reraNo, searchQuery) => {
  const normalizedBuilder = builderName && builderName !== "All Builders" ? builderName : "";
  const normalizedReraNo = (reraNo || "").trim();
  const normalizedSearchQuery = (searchQuery || "").trim();
  return normalizedReraNo
    ? `reraNo:${normalizedReraNo}`
    : normalizedSearchQuery || (normalizedBuilder ? `builderName:${normalizedBuilder}` : "");
};

const normalizeFieldKey = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const formatDatabaseValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (Array.isArray(value)) {
    return Array.from(new Set(value.map((entry) => formatDatabaseValue(entry)))).filter((entry) => entry !== "-").join(", ") || "-";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  const formatted = String(value).trim();
  return !formatted || /^(n\/a|na|null|undefined|no)$/i.test(formatted) ? "-" : formatted;
};

const findDatabaseEntry = (source, targetKeys) => {
  if (!source || typeof source !== "object") {
    return undefined;
  }

  const normalizedTargets = targetKeys.map(normalizeFieldKey);
  const stack = [source];
  const seen = new Set();

  while (stack.length > 0) {
    const current = stack.pop();

    if (!current || typeof current !== "object" || seen.has(current)) {
      continue;
    }

    seen.add(current);

    for (const [key, value] of Object.entries(current)) {
      const normalizedKey = normalizeFieldKey(key);

      if (normalizedTargets.includes(normalizedKey) && value !== null && value !== undefined && String(value).trim() !== "") {
        return { key, value };
      }

      if (value && typeof value === "object") {
        stack.push(value);
      }
    }
  }

  return undefined;
};

const findDatabaseValue = (source, targetKeys) => findDatabaseEntry(source, targetKeys)?.value;

const getProjectCoordinates = (source) => {
  const latitudeValue = findDatabaseValue(source, ["Latitude", "Lat"]);
  const longitudeValue = findDatabaseValue(source, ["Longitude", "Long", "Lng"]);
  if (latitudeValue === undefined || latitudeValue === null || String(latitudeValue).trim() === ""
    || longitudeValue === undefined || longitudeValue === null || String(longitudeValue).trim() === "") {
    return null;
  }

  const latitude = Number(latitudeValue);
  const longitude = Number(longitudeValue);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)
    || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return null;
  }

  const isInIndia = (lat, lon) => lat >= INDIA_MAP_BOUNDS.minLatitude
    && lat <= INDIA_MAP_BOUNDS.maxLatitude
    && lon >= INDIA_MAP_BOUNDS.minLongitude
    && lon <= INDIA_MAP_BOUNDS.maxLongitude;

  if (latitude === 0 && longitude === 0) {
    return null;
  }

  if (!isInIndia(latitude, longitude)) {
    if (!isInIndia(longitude, latitude)) {
      return null;
    }

    return { latitude: longitude, longitude: latitude };
  }

  return { latitude, longitude };
};

const findDatabaseField = (source, targetKeys) => formatDatabaseValue(findDatabaseValue(source, targetKeys));

const formatBooleanField = (source, targetKeys) => {
  const value = findDatabaseValue(source, targetKeys);
  if (value === undefined) {
    return "-";
  }

  if (typeof value === "boolean" || typeof value === "number" || typeof value === "string") {
    const normalized = String(value).trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(normalized)) {
      return "Yes";
    }
    if (["false", "0", "no", "n"].includes(normalized)) {
      return "-";
    }
  }

  return formatDatabaseValue(value);
};

const formatProjectAddress = (value) => {
  const segments = Array.isArray(value) ? value : value && typeof value === "object" ? Object.values(value) : String(value || "").split(",");
  const uniqueSegments = new Map();

  segments.forEach((segment) => {
    const normalized = formatDatabaseValue(segment);
    if (normalized !== "-" && !/^(null|undefined)$/i.test(normalized)) {
      uniqueSegments.set(normalized.toLowerCase(), normalized);
    }
  });

  return Array.from(uniqueSegments.values()).join(", ");
};

const mapProjectToCard = (project, index) => {
  const { status, statusClass } = deriveStatus(project);
  const approvals = normalizeApprovals(project?.Approvals);
  const planApprovalNumber = getPlanApprovalNumber(project);
  const projectType = formatDatabaseValue(project?.ProjectType || project?.Type);
  const proposedCompletionDate = formatProjectDate(findDatabaseValue(project, ["ProposedDateOfCompletion", "ProposedCompletionDate", "ProposedCompletion", "ExpectedCompletionDate"]));
  const reraRegistrationNumber = findDatabaseField(project, ["RERANo", "ReraNumber", "ReraNo", "ReraId", "ReraRegistrationNumber", "RegistrationNumber"]);
  const certificateNumber = findDatabaseField(project, ["CertificateNumber", "ReraCertificateNumber", "ReraCertificateNo", "RegistrationCertificateNumber", "CertificateNo"]);
  const registrationDisplay = reraRegistrationNumber !== "-"
    ? { label: "RERA Registration No.", value: reraRegistrationNumber }
    : { label: "Application Number", value: certificateNumber };
  const createdDate = formatProjectDate(findDatabaseValue(project, ["CreatedDate", "CreatedOn", "CreateDate"]));
  const updatedDate = formatProjectDate(findDatabaseValue(project, ["UpdatedDate", "UpdatedOn", "ModifiedDate", "ModifiedOn"]));
  const rawAddress = findDatabaseValue(project, ["FullAddress", "Address", "ProjectAddress"]);
  const composedAddress = rawAddress ?? [
    findDatabaseValue(project, ["AddressLine1"]),
    findDatabaseValue(project, ["AddressLine2"]),
    findDatabaseValue(project, ["Street", "Locality"]),
    findDatabaseValue(project, ["Village", "VillageName"]),
    findDatabaseValue(project, ["Mandal", "Taluk"]),
    findDatabaseValue(project, ["District"]),
    findDatabaseValue(project, ["State"]),
    findDatabaseValue(project, ["Pincode", "PinCode", "PostalCode"])
  ].filter((value) => value !== undefined);

  return {
    id: project?.ProjectId || project?.Id || `${project?.ProjectName || "project"}-${index}`,
    status,
    statusClass,
    img: getCategoryImage(project, projectType),
    name: formatDatabaseValue(project?.ProjectName) === "-" ? "Unnamed Project" : formatDatabaseValue(project?.ProjectName),
    builder: formatDatabaseValue(project?.BuilderName) === "-" ? "Builder details unavailable" : formatDatabaseValue(project?.BuilderName),
    location: formatProjectAddress(composedAddress) || "Location unavailable",
    projectType,
    proposedCompletionDate,
    registrationDisplay,
    planApprovalNumber: planApprovalNumber || "-",
    createdDate,
    updatedDate,
    reraValidity: formatBooleanField(project, ["ReraValidity", "IsReraValid", "ReraValid", "ReraValidTill", "IsReraVerified", "ReraStatus"]),
    projectData: project,
    tags: approvals.map((approval, approvalIndex) => ({
      key: `${approval.toLowerCase().replace(/\s+/g, "-")}-${approvalIndex}`,
      label: approval,
      tooltip: planApprovalNumber ? `Plan Approval Number: ${planApprovalNumber}` : "Plan Approval Number: -"
    })),
    aiLabel: formatDatabaseValue(project?.AIInsight) === "-" ? "No AI insight available" : formatDatabaseValue(project?.AIInsight),
    aiDetail: ""
  };
};

function PropertySection({ builderName = "All Builders", reraNo = "", searchQuery = "", filters = {} }) {
  const [view, setView] = useState("list");
  const [favs, setFavs] = useState(/* @__PURE__ */ new Set());
  const [projects, setProjects] = useState([]);
  const [mapProjects, setMapProjects] = useState([]);
  const [mapError, setMapError] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [pendingProject, setPendingProject] = useState(null);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState("");

  const mappedProjects = useMemo(() => projects.map(mapProjectToCard), [projects]);
  const visibleProjects = useMemo(() => {
    if (mappedProjects.length <= PAGE_SIZE) {
      return mappedProjects;
    }

    const startIndex = (pageNumber - 1) * PAGE_SIZE;
    return mappedProjects.slice(startIndex, startIndex + PAGE_SIZE);
  }, [mappedProjects, pageNumber]);
  const mapProjectsWithCoordinates = useMemo(
    () => mapProjects.map(mapProjectToCard).filter((project) => getProjectCoordinates(project.projectData)),
    [mapProjects]
  );
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const hasPreviousPage = pageNumber > 1;
  const hasNextPage = totalCount > pageNumber * PAGE_SIZE;

  const loadProjects = useCallback(async (page, activeBuilderName, activeReraNo, activeSearchQuery, activeFilters) => {
    setIsLoading(true);
    setError("");

    try {
      const result = await searchVentures({
        query: getProjectSearchQuery(activeBuilderName, activeReraNo, activeSearchQuery),
        pageNumber: page,
        pageSize: PAGE_SIZE,
        filters: activeFilters
      });

      setProjects(result.items);
      setTotalCount(result.totalCount);
    } catch (apiError) {
      setProjects([]);
      setTotalCount(0);
      setError(apiError?.message || "Unable to load projects right now.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setPageNumber(1);
  }, [builderName, reraNo, searchQuery, filters]);

  useEffect(() => {
  loadProjects(pageNumber, builderName, reraNo, searchQuery, filters);
  }, [builderName, filters, loadProjects, pageNumber, reraNo, searchQuery]);

  useEffect(() => {
  let isCurrentRequest = true;
  const loadMapProjects = async () => {
    setMapProjects([]);
    setMapError("");

    try {
      const query = getProjectSearchQuery(builderName, reraNo, searchQuery);
      const firstPage = await searchVentures({
        query,
        pageNumber: 1,
        pageSize: MAP_PAGE_SIZE,
        filters
      });
      let allProjects = firstPage.items;

      if (firstPage.totalCount > firstPage.items.length) {
        const pageCount = Math.ceil(firstPage.totalCount / MAP_PAGE_SIZE);
        const remainingPages = await Promise.all(Array.from({ length: pageCount - 1 }, (_, index) => searchVentures({
          query,
          pageNumber: index + 2,
          pageSize: MAP_PAGE_SIZE,
          filters
        })));
        allProjects = allProjects.concat(...remainingPages.map((result) => result.items));
      }

      if (isCurrentRequest) {
        setMapProjects(allProjects);
      }
    } catch (mapLoadError) {
      if (isCurrentRequest) {
        setMapError(mapLoadError?.message || "Unable to load all project locations.");
      }
    }
  };

  loadMapProjects();
  return () => {
    isCurrentRequest = false;
  };
  }, [builderName, filters, reraNo, searchQuery]);

  const toggleFav = (id) => setFavs((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const projectData = selectedProject?.projectData;
  const detailValue = (keys) => findDatabaseField(projectData, keys);
  const detailDate = (keys) => formatProjectDateTime(findDatabaseValue(projectData, keys));
  const totalAreaEntry = findDatabaseEntry(projectData, ["TotalAreaSqm", "TotalAreaSqM", "TotalArea", "TotalProjectArea", "ProjectArea", "TotalLandArea", "LandArea"]);
  const totalArea = totalAreaEntry?.value;
  const totalAreaKey = normalizeFieldKey(totalAreaEntry?.key);
  const totalAreaLabel = totalArea === undefined
    ? "-"
    : `${formatNumberValue(totalArea)}${totalAreaKey === "totalareasqm" ? " sqm" : ""}`;
  const coordinates = getProjectCoordinates(projectData);
  const hasCoordinates = Boolean(coordinates);
  const mapUrl = hasCoordinates
    ? `https://www.google.com/maps?q=${coordinates.latitude},${coordinates.longitude}`
    : "";
  const approvalAuthority = detailValue(["ApprovalAuthority", "ApprovalAuthorityName", "ApprovalAuthorities", "AuthorityName", "PlanApprovalAuthority"]);
  const approvalsValue = selectedProject?.tags.map((tag) => tag.label).join(", ") || "-";
  const reraNumber = detailValue(["RERANo", "ReraNumber", "ReraNo", "ReraId", "ReraRegistrationNumber", "RegistrationNumber"]);
  const planApprovalNumber = getPlanApprovalNumber(projectData) || "-";
  const certificateNumber = detailValue(["CertificateNumber", "ReraCertificateNumber", "ReraCertificateNo", "RegistrationCertificateNumber", "CertificateNo"]);
  const officialAddress = detailValue(["AddressForOfficialCommunication", "OfficialCommunicationAddress", "CommunicationAddress", "AddressForCommunication", "OfficialAddress"]);
  const projectFlagFields = [
    { label: "RERA Registered", value: formatBooleanField(projectData, ["HasRERA"]) },
    { label: "Bank Loan Available", value: formatBooleanField(projectData, ["HasBankLoan", "BankLoanAvailable", "IsBankLoanAvailable"]) },
    { label: "New Launch", value: formatBooleanField(projectData, ["IsNewLaunch"]) },
    { label: "Ready to Move", value: formatBooleanField(projectData, ["IsReadyToMove", "ReadyToMove"]) },
    { label: "Under Construction", value: formatBooleanField(projectData, ["IsUnderConstruction", "UnderConstruction"]) }
  ];

  const registrationFields = [
    { label: "RERA Registration Number", value: reraNumber },
    { label: "LP / Plan Approval Number", value: planApprovalNumber },
    { label: "Application Number", value: certificateNumber },
    { label: "Approval Authority", value: approvalAuthority },
    { label: "Approvals Listed", value: approvalsValue },
    { label: "RERA Validity Flag", value: formatBooleanField(projectData, ["IsRERAValid", "ReraValidity", "IsReraValid", "ReraValid", "IsReraVerified"]) },
    { label: "RERA Registered Date", value: detailDate(["RERARegisteredDate", "ReraRegisteredDate"]) },
    { label: "RERA Expiry Date", value: detailDate(["RERAExpiryDate", "ReraExpiryDate", "ReraValidTill"]) },
    { label: "Has RERA (Database Flag)", value: formatBooleanField(projectData, ["HasRERA"]) }
  ];

  const constructionFields = [
    { label: "Total Land Area", value: totalAreaLabel },
    { label: "Built-up Area", value: formatNumberValue(findDatabaseValue(projectData, ["BuiltupArea", "BuiltUpArea", "BuiltUpAreaDetails", "BuiltUpAreaSqFt", "BuiltUpAreaSqM"])) },
    { label: "Total Units / Plots", value: formatNumberValue(findDatabaseValue(projectData, ["TotalUnits", "TotalUnit", "TotalNumberOfUnits", "NumberOfUnits", "NoOfUnits", "UnitCount"]), 0) },
    { label: "Project Type", value: selectedProject?.projectType || "-" },
    { label: "Project Status", value: selectedProject?.status || "-" },
    { label: "Project Record ID", value: detailValue(["Id", "ProjectId"]) }
  ];

  const timelineFields = [
    { label: "Proposed Date of Completion", value: detailDate(["ProposedCompletion", "ProposedDateOfCompletion", "ProposedCompletionDate", "ExpectedCompletionDate"]) },
    { label: "Revised Proposed Completion", value: detailDate(["RevisedCompletion", "RevisedProposedDateOfCompletion", "RevisedProposedCompletionDate", "RevisedCompletionDate"]) },
    { label: "Created Date", value: detailDate(["CreatedDate", "CreatedOn", "CreateDate"]) },
    { label: "Last Updated Date", value: detailDate(["UpdatedDate", "UpdatedOn", "ModifiedDate", "ModifiedOn"]) }
  ];

  const locationFields = [
    { label: "Full Address", value: selectedProject?.location || "-" },
    { label: "Locality", value: detailValue(["Locality", "Street", "AddressLine1"]) },
    { label: "Village", value: detailValue(["Village", "VillageName"]) },
    { label: "Mandal", value: detailValue(["Mandal", "Taluk"]) },
    { label: "District", value: detailValue(["District"]) },
    { label: "Pincode", value: detailValue(["Pincode", "PinCode", "PostalCode", "ZipCode"]) },
    { label: "Latitude", value: formatDatabaseValue(coordinates?.latitude) },
    { label: "Longitude", value: formatDatabaseValue(coordinates?.longitude) }
  ];

  const additionalFields = [
    { label: "Feature", value: detailValue(["Feature", "Features"]) },
    { label: "Official Communication Address", value: officialAddress === "-" ? "Not supplied by API" : formatProjectAddress(findDatabaseValue(projectData, ["AddressForOfficialCommunication", "OfficialCommunicationAddress", "CommunicationAddress", "AddressForCommunication", "OfficialAddress"])) },
    { label: "Database Approval Date", value: "Not supplied by API" }
  ];

  const copyProjectDetails = async () => {
    if (!selectedProject) {
      return;
    }

    const fields = [
      ["Project", selectedProject.name],
      ["Builder", selectedProject.builder],
      ["Project ID", detailValue(["Id", "ProjectId"])],
      ["Project Type", selectedProject.projectType],
      ["Project Status", selectedProject.status],
      ["Address", selectedProject.location],
      ["RERA Number", reraNumber],
      ["LP Number", planApprovalNumber],
      ["Application Number", certificateNumber],
      ["Approval Authority", approvalAuthority],
      ["Approvals", approvalsValue],
      ["Land Area", totalAreaLabel],
      ["Built-up Area", constructionFields[1].value],
      ["Total Units", constructionFields[2].value],
      ["Proposed Completion", timelineFields[0].value],
      ["Revised Completion", timelineFields[1].value],
      ...projectFlagFields.map(({ label, value }) => [label, value]),
      ["AI Insight", selectedProject.aiLabel]
    ];
    const text = fields.map(([label, value]) => `${label}: ${value}`).join("\n");

    if (!navigator.clipboard?.writeText) {
      window.prompt("Copy project details:", text);
      setCopyStatus("Project details are ready to copy.");
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus("Project details copied.");
    } catch {
      setCopyStatus("Unable to copy details. Check browser clipboard permissions.");
    }
  };

  const openProjectDetails = (project) => {
    setCopyStatus("");
    setSelectedProject(project);
  };

  const handleViewDetails = (project) => {
    if (hasActiveSession()) {
      openProjectDetails(project);
      return;
    }

    setPendingProject(project);
    setIsLoginOpen(true);
  };

  const handleLoginSuccess = () => {
    setIsLoginOpen(false);
    if (pendingProject) {
      openProjectDetails(pendingProject);
    }
    setPendingProject(null);
  };

  const handleLoginClose = () => {
    setIsLoginOpen(false);
    setPendingProject(null);
  };


  return <section style={{ padding: "0 20px" }}>
      {
    /* Section Header */
  }
      <div className="vd-section-bar">
        <p className="vd-result-label mb-0">
          Showing <strong>{totalCount}</strong> projects matching your search
          {isLoading ? " (Loading...)" : ""}
        </p>
        <div className="vd-view-toggle" role="tablist" aria-label="Project display">
          <button
            type="button"
            role="tab"
            id="projects-list-tab"
            aria-selected={view === "list"}
            aria-controls="projects-list-panel"
            className={`vd-view-btn${view === "list" ? " active" : ""}`}
            onClick={() => setView("list")}
          >
            <FontAwesomeIcon icon={faList} aria-hidden="true" />
            List View
          </button>
          <button
            type="button"
            role="tab"
            id="projects-map-tab"
            aria-selected={view === "map"}
            aria-controls="projects-map-panel"
            className={`vd-view-btn${view === "map" ? " active" : ""}`}
            onClick={() => setView("map")}
          >
            <FontAwesomeIcon icon={faMap} aria-hidden="true" />
            Map View
          </button>
        </div>
      </div>

      {view === "list" && <div id="projects-list-panel" role="tabpanel" aria-labelledby="projects-list-tab" className="row g-3">
        <div className="col-12">
          {error ? <div style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 14,
            padding: 16,
            color: "#b42318"
          }}>
              <div>{error}</div>
              <button className="vd-more-projects-btn" style={{ marginTop: 10 }} onClick={() => loadProjects(pageNumber, builderName, reraNo, searchQuery, filters)}>
                Retry
              </button>
            </div> : null}

          {!error && !isLoading && visibleProjects.length === 0 ? <div style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 14,
            padding: 16,
            color: "#475467"
          }}>
              No projects found for this search.
            </div> : null}

          {!error && visibleProjects.length > 0 ? <div className="row g-3">
              {visibleProjects.map((p) => <div key={p.id} className="col-12 col-sm-6 col-md-4 col-xl-3">
                  <PropertyCard
                    p={p}
                    faved={favs.has(p.id)}
                    onFav={() => toggleFav(p.id)}
                    onDetails={() => handleViewDetails(p)}
                  />
                </div>)}
            </div> : null}

          <div style={{ display: "flex", alignItems: "center", padding: "10px", backgroundColor: "#ececec", marginTop: "10px", borderRadius: "8px", gap: 10, flexWrap: "nowrap" }}>
            <button
              className="vd-more-projects-btn"
              style={{ width: "auto", marginTop: 0, flexShrink: 0 }}
              onClick={() => setPageNumber((prev) => Math.max(1, prev - 1))}
              disabled={isLoading || !hasPreviousPage}
            >
              Previous
            </button>
            <span style={{ fontSize: 12.5, color: "#6b7280" }}>
              Page {pageNumber} of {totalPages} ({PAGE_SIZE} per page)
            </span>
            <button
              className="vd-more-projects-btn"
              style={{ width: "auto", marginTop: 0, flexShrink: 0 }}
              onClick={() => setPageNumber((prev) => prev + 1)}
              disabled={isLoading || !hasNextPage}
            >
              {hasNextPage ? "Next" : "No More Projects"}
              <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 11 }} />
            </button>
          </div>
        </div>
      </div>}

      {view === "map" && <div id="projects-map-panel" role="tabpanel" aria-labelledby="projects-map-tab" className="row g-3">
        <div className="col-12" style={{ minHeight: 430 }}>
          <MapPane projects={mapProjectsWithCoordinates} error={mapError} />
        </div>
      </div>}

        <LoginModal open={isLoginOpen} onClose={handleLoginClose} onSuccess={handleLoginSuccess} />

        <Dialog open={Boolean(selectedProject)} onOpenChange={(open) => {
        if (!open) {
          setSelectedProject(null);
        }
      }}>
          <DialogContent className="sm:max-w-4xl p-0 overflow-hidden border-0 bg-white" style={{ maxHeight: "90vh", width: "min(96vw, 74rem)" }}>
            {selectedProject ? <div className="vd-project-details">
                <DialogHeader className="text-left modal_header gap-0" style={{ padding: "18px 24px 14px", borderBottom: "1px solid #e2e8f0", background: "#fff", flexShrink: 0 }}>
                  <DialogTitle style={{ fontSize: 22, color: "#0f172a" }}>Project Details</DialogTitle>
                  <DialogDescription style={{ color: "#64748b" }}>Project information and fields supplied by the database.</DialogDescription>
                </DialogHeader>

                <div className="vd-project-details-content">
                  <section className="vd-project-overview">
                    <div className="vd-project-overview-image">
                      <img src={selectedProject.img} alt={selectedProject.name} />
                      <span>Project ID: {detailValue(["Id", "ProjectId"])}</span>
                    </div>
                    <div className="vd-project-overview-body">
                      <div className="vd-project-eyebrow">Project Details</div>
                      <h2>{selectedProject.name}</h2>
                      <p className="vd-project-builder">{selectedProject.builder}</p>
                      <p className="vd-project-address">{selectedProject.location}</p>
                      <div className="vd-project-badges">
                        <span>{selectedProject.status}</span>
                        <span>{selectedProject.projectType}</span>
                        <span>RERA Registered: {formatBooleanField(projectData, ["HasRERA"])}</span>
                        <span>Approval: {approvalAuthority}</span>
                      </div>
                      <div className="vd-project-actions">
                        {mapUrl && <a className="vd-project-action primary" href={mapUrl} target="_blank" rel="noopener noreferrer">
                            <FontAwesomeIcon icon={faLocationDot} /> View Location
                          </a>}
                        <Button variant="outline" onClick={copyProjectDetails}>
                          <FontAwesomeIcon icon={faCopy} /> Copy Details
                        </Button>
                        {copyStatus && <span role="status" className="vd-project-copy-status">{copyStatus}</span>}
                      </div>
                    </div>
                  </section>

                  <section className="vd-project-insight" aria-label="Project intelligence">
                    <div>Project Intelligence</div>
                    <p>{selectedProject.aiLabel}</p>
                    <small>AI-generated insight is informational and is not a guarantee of investment performance.</small>
                  </section>

                  <DetailSection icon={faCircleCheck} title="Registration & Approvals" subtitle="RERA, layout permission and certificate details">
                    <DetailFields fields={registrationFields} />
                    <p className="vd-project-notice">Database flags and approval numbers are displayed as received from the API. Independently verify registrations and approvals before making a transaction.</p>
                  </DetailSection>

                  <DetailSection icon={faTableCells} title="Land & Construction Details" subtitle="Area, units and project configuration">
                    <DetailFields fields={constructionFields} />
                    <p className="vd-project-notice">Area units are shown explicitly where supplied by the source. Confirm the unit used for Built-up Area in the database before interpreting it.</p>
                  </DetailSection>

                  <DetailSection icon={faCalendarDays} title="Project Timeline" subtitle="Completion dates and record history">
                    <DetailFields fields={[
                      ...timelineFields,
                      { label: "Approved Date", value: "Not supplied by API" }
                    ]} />
                    <p className="vd-project-notice">An approved date is not present in the supplied API response. RERA Registered Date is a separate field and is not substituted for an approval date.</p>
                  </DetailSection>

                  <DetailSection icon={faLocationDot} title="Location & Official Address" subtitle="Geographical and address information">
                    <DetailFields fields={locationFields} />
                    {mapUrl && <div style={{ marginTop: 14 }}>
                        <a className="vd-project-action primary" href={mapUrl} target="_blank" rel="noopener noreferrer">
                          <FontAwesomeIcon icon={faLocationDot} /> Open Coordinates in Maps
                        </a>
                      </div>}
                  </DetailSection>

                  <DetailSection icon={faShieldHalved} title="Project Status & Facilities" subtitle="Availability flags and financing information">
                    <DetailStatusList fields={projectFlagFields} />
                  </DetailSection>

                  <DetailSection icon={faList} title="Additional Database Information" subtitle="Remaining fields from the project record">
                    <DetailFields fields={additionalFields} />
                  </DetailSection>
                </div>
              </div> : null}
            </DialogContent>
        </Dialog>
    </section>;
}
export {
  PropertySection as default
};
